import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import PaginationFooter from "../components/PaginationFooter";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as attendanceApi from "../api/attendance";
import "./AdminAttendance.css";

export default function AdminAttendance() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { hasPermission, loading: permLoading } = useViewPermission(
    "view_attendance",
  );
  const [courses, setCourses] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = DEFAULT_PAGE_SIZE;

  const fetchCourses = async (pageArg = page) => {
    try {
      const response = await attendanceApi.listCourses({
        page: pageArg,
        limit,
      });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setCourses(normalized.data);
      setPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching attendance courses:", err);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }
    fetchCourses(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="att-shell">
        <div className="att-banner">
          <div className="att-banner__row">
            <div>
              <h1 className="att-banner__title">ATTENDANCE COURSES</h1>
              <p className="att-banner__subtitle">
                Choose a course to take class attendance or review past records
              </p>
            </div>
          </div>
        </div>

        <div className="att-spacer" />

        {courses.length === 0 ? (
          <div className="att-empty">
            <div className="att-empty__title">No courses available</div>
            <div className="att-empty__hint">
              No courses match your attendance access. Enable attendance on a
              course from the Courses screen, or — if you use the
              standard_teacher role — ask an admin to link your account to a
              teacher.
            </div>
          </div>
        ) : (
          <>
            <div className="att-table-wrap">
              <div className="att-table-scroll">
                <table className="table align-middle mb-0 att-table">
                  <thead>
                    <tr>
                      <th>ACTIONS</th>
                      <th>COURSE</th>
                      <th>TEACHER</th>
                      <th>SESSION</th>
                      <th>YEAR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((course) => (
                      <tr key={course.id}>
                        <td>
                          <button
                            type="button"
                            className="att-btn att-btn--lavender"
                            onClick={() =>
                              navigate(`/admin/attendance/${course.id}`)
                            }
                          >
                            Open
                          </button>
                        </td>
                        <td
                          className="att-table__left"
                          style={{ fontWeight: 600 }}
                        >
                          {course.title}
                        </td>
                        <td>{course.Teacher?.name || "Teacher TBA"}</td>
                        <td>
                          {course.Session?.startMonth &&
                          course.Session?.endMonth
                            ? `${course.Session.startMonth}-${course.Session.endMonth}`
                            : "—"}
                        </td>
                        <td>{course.year || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <PaginationFooter
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={(p) => {
                setPage(p);
                fetchCourses(p);
              }}
              itemLabel="courses"
            />
          </>
        )}
      </div>
    </div>
  );
}
